import { useTranslation } from 'react-i18next';
import React, { ReactElement } from 'react';
import { getLocale } from 'i18n/helpers';

export type MoneyProps<E extends HTMLElement = HTMLSpanElement> = React.HTMLProps<E> & {
  tagName: keyof React.ReactHTML;
  placeholder?: string;
  amount?: number;
  locale?: string; // Locale code of the currency. If not provided then defaults to the current i18n locale
  disablePlaceholder?: boolean;
}

export function Money<E extends HTMLElement = HTMLSpanElement>({
  tagName, placeholder, amount, locale, disablePlaceholder, children, ...props
}: MoneyProps<E>): ReactElement | null {
  const { i18n } = useTranslation();

  let formattedValue = placeholder;
  if (amount !== undefined || disablePlaceholder) {
    // Utilize `t` for currency formatting so we can have different format strings for different locales
    // Invokes numeral in the i18n interpolation handler
    if (!locale) {
      locale = getLocale(i18n.language).code;
    }

    const t = i18n.getFixedT(locale);
    formattedValue = t('money', { amount: amount ?? 0 });
  }

  return React.createElement(tagName, props, formattedValue, children);
}
// eslint-disable-next-line @typescript-eslint/no-redeclare
type Money<E extends HTMLElement = HTMLSpanElement> = React.FC<MoneyProps<E>>;
// eslint-disable-next-line
Money.defaultProps = { tagName: 'span', placeholder: '—' };
